import React, { useState, useEffect } from 'react';
import config from '../config';

const MT5AccountManager = ({ onAccountAdded, onAccountChanged }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [formData, setFormData] = useState({
    account_number: '',
    broker_name: 'Swissquote',
    broker_server: 'Swissquote-Instit-Server',
    password: '',
    is_default: false
  });

  // Fetch all MT5 accounts for the current user
  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }      const response = await fetch(`${config.apiUrl}/mt5-accounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }

      const data = await response.json();
      console.log('🔍 MT5AccountManager - Raw API response:', data);
      
      // Debug: Check each account's EA status
      if (Array.isArray(data)) {
        data.forEach(account => {
          console.log(`🔍 Account ${account.account_number}:`, {
            ea_installed: account.ea_installed,
            ea_active: account.ea_active,
            is_default: account.is_default
          });
        });
      }
      
      setAccounts(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Add a new MT5 account
  const addAccount = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Validate form data
      if (!formData.account_number || !formData.password) {
        throw new Error('Account number and password are required');
      }

      const response = await fetch(`${config.apiUrl}/mt5-accounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          account_number: formData.account_number,
          broker_name: formData.broker_name,
          broker_server: formData.broker_server,
          password: formData.password,
          is_default: formData.is_default
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add account');
      }

      await response.json();
      
      // Reset form
      setFormData({
        account_number: '',
        broker_name: 'Swissquote',
        broker_server: 'Swissquote-Instit-Server',
        password: '',
        is_default: false
      });
      
      // Close the form
      setIsAddingAccount(false);
      
      // Refresh accounts list
      fetchAccounts();
      
      // Notify parent component
      if (onAccountAdded) {
        onAccountAdded();
      }
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  // Set an account as default
  const setDefaultAccount = async (accountId) => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${config.apiUrl}/mt5-accounts/${accountId}/default`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to set default account');
      }      // Refresh accounts list to get updated data
      await fetchAccounts();
      
      // Get the updated account that's now set as default
      const updatedAccount = accounts.find(acc => acc.id === accountId);
      
      // Notify parent component about the account change again with the latest data
      if (onAccountChanged && updatedAccount) {
        console.log(`Notifying parent of account change after API update: ${updatedAccount.account_number}`);
        onAccountChanged(updatedAccount);
      }
      
      // Also notify if an account was added (for backward compatibility)
      if (onAccountAdded) {
        onAccountAdded();
      }
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };  // Handle account selection
  const selectAccount = async (account) => {
    try {
      console.log(`Selecting account: ${JSON.stringify(account)}`);
      
      // Set error state to null when selecting account
      setError(null);
        // Store the selected account consistently using account number (not database ID)
      const accountId = account.account_number || account.id;
      localStorage.setItem('last_mt5_account_id', accountId);
      window.currentAccountId = accountId;
      
      // Set current account ID in a hidden input for other components
      const hiddenInput = document.getElementById('current-mt5-account') || 
                          document.createElement('input');
      hiddenInput.id = 'current-mt5-account';
      hiddenInput.type = 'hidden';
      hiddenInput.value = accountId;
      if (!hiddenInput.parentNode) {
        document.body.appendChild(hiddenInput);
      }
      
      // Always notify parent component immediately with latest account data
      if (onAccountChanged) {
        console.log(`Immediately notifying parent of account selection: ${account.account_number}`);
        onAccountChanged(account);
      }
      
      // Update backend default status if needed
      if (!account.is_default) {
        console.log(`Setting account ${account.account_number} as default in backend`);
        await setDefaultAccount(account.id);
        // setDefaultAccount will refresh accounts and call onAccountChanged again
      } else {
        console.log(`Account ${account.account_number} is already the default`);
        // For already-default accounts, just refresh to ensure data consistency
        await fetchAccounts();
      }
      
      // Dispatch events after backend operations are complete
      const accountSwitchEvent = new CustomEvent('mt5AccountChanged', { 
        detail: { 
          account: account,
          isDefault: account.is_default,
          forceRefresh: true 
        },
        bubbles: true 
      });
      document.dispatchEvent(accountSwitchEvent);
      
      // Also dispatch window event
      window.dispatchEvent(new CustomEvent('mt5AccountSwitched', { 
        detail: { 
          account: account,
          isDefault: account.is_default,
          forceRefresh: true 
        } 
      }));
      
      // Final force refresh event with a slight delay to ensure other events are processed
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('accountDataRequired', { 
          detail: { 
            accountId: accountId,
            forceRefresh: true 
          } 
        }));
      }, 200);
      
    } catch (err) {
      console.error(`Error selecting account: ${err.message}`);
      setError(err.message);
    }
  };

  // Load accounts on component mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700">MT5 Accounts</h3>
        <button
          onClick={() => setIsAddingAccount(!isAddingAccount)}
          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm"
        >
          {isAddingAccount ? 'Cancel' : '+ Add Account'}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {isAddingAccount && (
        <form onSubmit={addAccount} className="mb-6 p-4 border border-gray-200 rounded">
          <h4 className="font-medium mb-3">Add MT5 Account</h4>
          
          <div className="mb-3">
            <label htmlFor="account_number" className="block text-sm font-medium text-gray-700 mb-1">
              MT5 Account Number *
            </label>
            <input
              type="text"
              id="account_number"
              name="account_number"
              value={formData.account_number}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>
          
          <div className="mb-3">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              MT5 Password *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
              required
            />
          </div>
          
          <div className="mb-3">
            <label htmlFor="broker_name" className="block text-sm font-medium text-gray-700 mb-1">
              Broker Name
            </label>
            <input
              type="text"
              id="broker_name"
              name="broker_name"
              value={formData.broker_name}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
            />
          </div>
          
          <div className="mb-3">
            <label htmlFor="broker_server" className="block text-sm font-medium text-gray-700 mb-1">
              Broker Server
            </label>
            <select
              id="broker_server"
              name="broker_server"
              value={formData.broker_server}
              onChange={handleInputChange}
              className="w-full p-2 border border-gray-300 rounded"
            >
              <option value="Swissquote-Instit-Server">Swissquote-Instit-Server</option>
              <option value="Swissquote-Server">Swissquote-Server</option>
              <option value="Swissquote-Live">Swissquote-Live</option>
              <option value="Swissquote-Demo">Swissquote-Demo</option>
            </select>
          </div>
          
          <div className="mb-3">
            <label className="inline-flex items-center">
              <input
                type="checkbox"
                name="is_default"
                checked={formData.is_default}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700">Set as default account</span>
            </label>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            {loading ? 'Adding...' : 'Add Account'}
          </button>
        </form>
      )}

      <div>
        {loading && !isAddingAccount ? (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        ) : accounts.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No MT5 accounts found</p>
        ) : (          <div className="space-y-3">
            {accounts.map(account => (
              <div 
                key={account.id}
                className={`p-3 border rounded ${account.is_default ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} hover:bg-gray-50 cursor-pointer transition-colors`}
                onClick={(e) => {
                  e.preventDefault();
                  console.log(`Clicked on account ${account.account_number}`);
                  selectAccount(account);
                }}
                data-account-id={account.id}
                data-account-number={account.account_number}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium">{account.account_number}</div>
                    <div className="text-sm text-gray-600">{account.broker_name}</div>
                  </div>                  <div className="flex items-center">
                    {account.is_default ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">Default</span>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the parent click
                          setDefaultAccount(account.id);
                        }}
                        className="px-2 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-100"
                      >
                        Set as Default
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MT5AccountManager;
